import { useMemo, useState } from "react";
import { Input as AntInput } from "antd";
import styled from "styled-components";
import chevronDownUrl from "@/assets/icons/chevron-down.svg?url";
import infoUrl from "@/assets/icons/info.svg?url";
import { AppAutoComplete, AppSelect, AppTooltip, FormField } from "@/shared/components";
import { getBanksForCountry } from "@/features/creator/constants/banksByCountry";
import { TAP_TARGET, mediaPhone } from "@/shared/theme/breakpoints";
import type { BankFieldRequirements } from "../utils/bankRequirements";
import type { BankValidationErrors, BankValidationValues } from "../utils/validation";
import { pressedTint } from "@/shared/theme/pressed";

const ONBOARDING_POPUP_CLASS = "creator-onboarding-popup";

interface Props {
	active: boolean;
	compact?: boolean;
	country: string;
	bankCurrency: string;
	bank: BankValidationValues;
	errors: BankValidationErrors;
	requirements: BankFieldRequirements;
	onCountryChange: (country: string) => void;
	onBankChange: (field: keyof BankValidationValues, value: string) => void;
}

const Fields = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 24px;

	@media (max-width: 860px) {
		grid-template-columns: 1fr;
	}
`;
const BankFields = styled(Fields)<{ $compact?: boolean }>`
	margin-top: ${({ $compact }) => ($compact ? "12px" : "24px")};
	row-gap: ${({ $compact }) => ($compact ? "16px" : "24px")};
	column-gap: 24px;
`;

const Field = styled(FormField)<{ $wide?: boolean }>`
	min-width: 0;
	grid-column: ${({ $wide }) => ($wide ? "1 / -1" : "auto")};
`;

const Input = styled.input`
	width: 100%;
	height: 40px;
	padding: 8px 12px;
	border: 1px solid #cdd4f6;
	border-radius: 8px;
	outline: none;
	background: #fff;
	color: #171717;
	font: inherit;
	font-size: 16px;

	&:focus {
		border-color: #9fadf1;
	}
`;

const StyledSelect = styled(AppSelect)`
	width: 100%;
	height: 40px;
	.ant-select-selector {
		height: 40px !important;
		border-color: #cdd4f6 !important;
		border-radius: 8px !important;
	}
	.ant-select-selection-item,
	.ant-select-selection-placeholder {
		font-size: 16px !important;
		line-height: 38px !important;
	}
	.ant-select-selection-search-input {
		font-size: 16px !important;
	}
`;

const BankAutoComplete = styled(AppAutoComplete)`
	display: block;
	width: 100%;
	height: 40px !important;

	.ant-select-selector {
		display: block !important;
		height: 40px !important;
		padding: 0 !important;
		border: 0 !important;
		background: transparent !important;
		box-shadow: none !important;
	}

	.ant-select-selection-search {
		display: block !important;
		inset: 0 !important;
	}
`;

const BankNameInput = styled(AntInput)`
	&& {
		display: flex;
		align-items: center;
		box-sizing: border-box;
		height: 40px;
		min-height: 40px;
		padding: 0 12px;
		border: 1px solid #cdd4f6;
		border-radius: 8px;
		background: #fff;
		box-shadow: none;
		font: inherit;
		font-size: 16px;

		&:hover,
		&:focus-within {
			border-color: #9fadf1;
			box-shadow: none;
		}
	}

	.ant-input {
		height: 38px;
		padding: 0;
		border: 0;
		background: transparent;
		box-shadow: none;
		color: #171717;
		font: inherit;
		font-size: 16px;
		line-height: normal;
	}

	.ant-input-suffix {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		margin-inline-start: 8px;
	}

	.ant-input-clear-icon {
		display: inline-flex;
		align-items: center;
		margin: 0;
		font-size: 12px;
	}
`;

const BankMenuButton = styled.button<{ $open: boolean }>`
	display: inline-grid;
	place-items: center;
	width: 24px;
	height: 24px;
	padding: 0;
	border: 0;
	background: transparent;
	color: #171717;
	cursor: pointer;

	img {
		width: 24px;
		height: 24px;
		transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
		transition: transform 0.15s ease;
	}

	&:focus-visible {
		border-radius: 4px;
		outline: 2px solid #9fadf1;
		outline-offset: 1px;
	}

	${pressedTint}
`;
const InfoIcon = styled.img`
	width: 15px;
	height: 15px;
	margin-left: 4px;
	vertical-align: -3px;
	cursor: help;
`;

/** Gives the info icon a 44px tap area on phones; a tap opens the tooltip, not the labelled input. */
const InfoTrigger = styled.span`
	display: inline;
	cursor: help;

	${mediaPhone`
		position: relative;

		&::after {
			content: "";
			position: absolute;
			inset: calc((${TAP_TARGET}px - 100%) / -2);
		}
	`}
`;

const Heading = styled.h2`
	margin: 0 0 8px;
	color: #303030;
	font-size: 18px;
	font-weight: 600;
	line-height: 1.3;
`;

const Description = styled.p`
	margin: 0 0 24px;
	color: #707070;
	font-size: 12px;
	line-height: 1.45;
`;

export const OnboardingBankDetailsStep = ({
	active,
	compact = false,
	country,
	bankCurrency,
	bank,
	errors,
	requirements,
	onCountryChange,
	onBankChange,
}: Props) => {
	const [bankMenuOpen, setBankMenuOpen] = useState(false);
	const [countryMenuOpen, setCountryMenuOpen] = useState(false);
	const countryBanks = useMemo(() => getBanksForCountry(country).map((value) => ({ value })), [country]);
	return (
		<>
			<Heading id="creator-onboarding-title-bank">Bank details</Heading>
			<Description>
				Creator Payments International Pte. Ltd. uses these to prepare your Payment Statement.
			</Description>
			<StyledSelect
				aria-label="Bank country"
				value={country}
				open={active && countryMenuOpen}
				onOpenChange={setCountryMenuOpen}
				getPopupContainer={() => document.body}
				popupClassName={ONBOARDING_POPUP_CLASS}
				options={[
					{ value: "SG", label: "Singapore" },
					{ value: "AU", label: "Australia" },
					{ value: "PH", label: "Philippines" },
					{ value: "US", label: "United States" },
					{ value: "ID", label: "Indonesia" },
					{ value: "VN", label: "Vietnam" },
					{ value: "MY", label: "Malaysia" },
					{ value: "TH", label: "Thailand" },
				]}
				onChange={(value) => onCountryChange(String(value))}
			/>
			<BankFields $compact={compact}>
				<Field
					$wide
					htmlFor="creator-onboarding-account-name"
					label="Account Name"
					error={errors.accountName}
					required
				>
					<Input
						id="creator-onboarding-account-name"
						value={bank.accountName}
						onChange={(event) => onBankChange("accountName", event.target.value)}
					/>
				</Field>
				<Field htmlFor="creator-onboarding-bank-name" label="Bank Name" error={errors.bankName} required>
					<BankAutoComplete
						id="creator-onboarding-bank-name"
						value={bank.bankName}
						options={countryBanks}
						open={active && bankMenuOpen}
						onOpenChange={setBankMenuOpen}
						onFocus={() => setBankMenuOpen(true)}
						defaultActiveFirstOption={false}
						placeholder="Select or enter your bank"
						filterOption={(input, option) =>
							String(option?.value ?? "")
								.toLowerCase()
								.includes(input.trim().toLowerCase())
						}
						getPopupContainer={() => document.body}
						popupClassName={ONBOARDING_POPUP_CLASS}
						onChange={(value) => onBankChange("bankName", String(value))}
						onSelect={(value) => {
							onBankChange("bankName", String(value));
							setBankMenuOpen(false);
						}}
					>
						<BankNameInput
							allowClear
							autoComplete="off"
							suffix={
								<BankMenuButton
									$open={bankMenuOpen}
									type="button"
									aria-label="Open bank options"
									aria-expanded={bankMenuOpen}
									onMouseDown={(event) => {
										event.preventDefault();
										event.stopPropagation();
									}}
									onClick={(event) => {
										event.stopPropagation();
										setBankMenuOpen(true);
									}}
								>
									<img src={chevronDownUrl} alt="" />
								</BankMenuButton>
							}
						/>
					</BankAutoComplete>
				</Field>
				<Field
					htmlFor="creator-onboarding-account-number"
					label="Account Number"
					error={errors.accountNumber}
					required
				>
					<Input
						id="creator-onboarding-account-number"
						value={bank.accountNumber}
						onChange={(event) => onBankChange("accountNumber", event.target.value)}
					/>
				</Field>
				{requirements.bsb && (
					<Field htmlFor="creator-onboarding-bsb" label="BSB Code" error={errors.bsb} required>
						<Input
							id="creator-onboarding-bsb"
							value={bank.bsb}
							onChange={(event) => onBankChange("bsb", event.target.value)}
						/>
					</Field>
				)}
				{requirements.abaRoutingNumber && (
					<Field
						htmlFor="creator-onboarding-aba-routing-number"
						label="ABA Routing Number"
						error={errors.abaRoutingNumber}
						required
					>
						<Input
							id="creator-onboarding-aba-routing-number"
							value={bank.abaRoutingNumber}
							onChange={(event) => onBankChange("abaRoutingNumber", event.target.value)}
						/>
					</Field>
				)}
				{requirements.branchCode && (
					<Field
						htmlFor="creator-onboarding-branch-code"
						label="Branch Code"
						error={errors.branchCode}
						required
					>
						<Input
							id="creator-onboarding-branch-code"
							value={bank.branchCode}
							onChange={(event) => onBankChange("branchCode", event.target.value)}
						/>
					</Field>
				)}
				{requirements.branchName && (
					<Field
						htmlFor="creator-onboarding-branch-name"
						label="Branch Name"
						error={errors.branchName}
						required
					>
						<Input
							id="creator-onboarding-branch-name"
							value={bank.branchName}
							onChange={(event) => onBankChange("branchName", event.target.value)}
						/>
					</Field>
				)}
				<Field
					htmlFor="creator-onboarding-swift-bic"
					label={
						<span>
							SWIFT/BIC Code{" "}
							<AppTooltip
								title="Your bank’s SWIFT/BIC code identifies it for international payments. You can find it on your bank statement or by contacting your bank."
								trigger={["hover", "click"]}
								showArrow={false}
								zIndex={2600}
								getPopupContainer={() => document.body}
							>
								<InfoTrigger onClick={(event) => event.preventDefault()}>
									<InfoIcon src={infoUrl} alt="" />
								</InfoTrigger>
							</AppTooltip>
						</span>
					}
					error={errors.swiftBic}
					required
				>
					<Input
						id="creator-onboarding-swift-bic"
						value={bank.swiftBic}
						onChange={(event) => onBankChange("swiftBic", event.target.value)}
					/>
				</Field>
				<Field htmlFor="creator-onboarding-bank-currency" label="Bank Currency" required>
					<Input id="creator-onboarding-bank-currency" value={bankCurrency} readOnly />
				</Field>
			</BankFields>
		</>
	);
};
